import React, { useEffect, useState } from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
  Box,
  Typography,
  List,
  Pagination,
  TextField,
  Button,
  Container,
  Grid,
  TablePagination,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SyncIcon from '@mui/icons-material/Sync';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';

import {getMails, downloadMailboxAttachment, downloadCertificadoRetentionPDF, getMailById, markMailAsRead} from '../../../../api/buzon/BuzonService';

import ViewMailsModal from '../Modals/ViewMailsModal';
import SnackBarError from '../../../Shared/SnackBarError/SnackBarError';
import s from "../Buzon.module.css";
import ConcentratorSelectAgencyModal from '../../../Shared/ConcentratorSelectAgencyModal/ConcentratorSelectAgencyModal';
import { agencieSearchByNumber } from '../../../../api/agencies/AgenciesService';
import {setConcentrator, setSelectedAgencieToConsult, decrementUnreadMailCount} from "../../../../store/slices/my_agencies";


/** Categorías de buzón. Debe mantenerse sincronizado con la tabla mailbox_kinds del backend. */
const MAILBOX_KIND_OPTIONS = [
  { id: '',  label: 'Todas' },
  { id: 0,   label: 'Varios' },
  { id: 1,   label: 'Circulares' },
  { id: 2,   label: 'Documentación' },
  { id: 3,   label: 'Extractos' },
  { id: 4,   label: 'Liquidaciones' },
  { id: 5,   label: 'Solicitudes' },
  { id: 6,   label: 'Certificados de IBB' },
];

const MailList = () => {
  
  const dispatch = useDispatch();
  const { user_agencies } = useSelector((state) => state.myAgencies);
  const agencieSelected = useSelector((state) => state.myAgencies?.selected_agencie_to_consult);
  const { activeOrganization } = useSelector((state) => state.gamma);
  const currentUser = useSelector((state) => state.gamma.logged_user);

  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);
   const [showHideAgencySelectModal, setShowHideAgencySelectModal] = useState(false);

   const [loadingAgency, setLoadingAgency] = useState(false);
   const [search, setSearch] = useState(false);

  // '' representa "Todas las categorías" (sin filtro)
  const [selectedKind, setSelectedKind] = useState('');

  // Pagination
  const [controllerPagination, setControllerPagination] = useState({
    page: 0,
    size: 10
  })
  const [totalElements, setTotalElements] = useState(0)

  // Modal
  const [open, setOpen] = React.useState(false);
  const [selectedMailId, setSelectedMailId] = React.useState(null);


  const handleOpen = (id) => {
    setSelectedMailId(id);
    setOpen(true);
    // El acuse de lectura solo lo genera el Usuario Concentradora (requerimiento certificados IIBB).
    // Un agenciero puede abrir/descargar el PDF, pero NO marca "visto" ni informa acuse a Gamma.
    // Se valida por rol del usuario logueado (no por agencyLevel de la agencia seleccionada),
    // porque un mismo usuario puede consultar agencias de distinto nivel y la política es por rol.
    // Match tolerante ("usuario concentr...") por si el nombre exacto del rol difiere entre entornos
    // (ej. "Usuario Concentradora" vs "Usuario Concentrador").
    const isConcentradora = currentUser?.memberships?.[0]?.roles?.some(
      (r) => r?.name?.toLowerCase().includes("usuario concentr")
    );
    const mail = mails.find(m => m.id === id);
    if (mail && !mail.isRead && isConcentradora) {
      dispatch(decrementUnreadMailCount());
      // Persistir en el backend usando el mailboxDestinationId
      if (mail.mailboxDestinationId) {
        markMailAsRead(mail.mailboxDestinationId);
      }
      setMails(prev => prev.map(m => m.id === id ? { ...m, isRead: true } : m));
    }
  };

  const handleClose = () => setOpen(false);
  

  // SnackBarError
  const [state, setState] = React.useState({
      open: false,
      vertical: 'top',
      horizontal: 'center',
      message: ''
    });
  
  const handleOpenSnackBarError = (newState) => () => {
    setState({ ...newState, open: true });
  };

  const handleCloseSnackBarError = () => {
    setState({ ...state, open: false });
  };

  // FN para checkear el formato de fecha
  const formatDate = (date) => {
    if (date && typeof date.format === 'function') {
      // tipo dayjs o moment
      return date.format('YYYY-MM-DD HH:mm');
    } else if (date instanceof Date) {
      // JS Date nativo
      return date.toLocaleString('sv-SE', { hour12: false }).replace('T', ' ').slice(0, 16);
    } else if (typeof date === 'string') {
      // ISO string
      return date.slice(0, 16).replace('T', ' ');
    }
    return '';
  };

  const handleToggleAgencySelectModal = () => setShowHideAgencySelectModal(!showHideAgencySelectModal);

  const handleChangePage = (
    _event,
    newPage,
  ) => {
    setControllerPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event,
  ) => {
    setControllerPagination((prev) => ({ ...prev, size: parseInt(event.target.value, 10) }));
  };

  /** Resetea la paginación a la primera página y recarga con la nueva categoría */
  const handleKindChange = (event) => {
    setSelectedKind(event.target.value);
    setControllerPagination((prev) => ({ ...prev, page: 0 }));
  };

  useEffect(()=>{
    fetchMails(agencieSelected)
  },[])

  useEffect(() => {
    fetchMails(agencieSelected);
  // Re-fetch cuando cambia la página o la categoría seleccionada
  }, [controllerPagination, selectedKind]);

  const fetchMails = async (agencieSelected) => {
    setLoading(true);
    try {
        const params = {
          agencyId: agencieSelected.id,
          page: controllerPagination.page,
          size: controllerPagination.size,
          // Solo envía mailboxKindId si hay una categoría seleccionada ('' = todas)
          ...(selectedKind !== '' && { mailboxKindId: selectedKind }),
        }
        const data = await getMails(params);
        setMails(data.content);
        setTotalElements(data.totalElements);
    } catch (error) {
      const message = error.response?.data?.message || "Error fetching mails";
      handleOpenSnackBarError({ vertical: 'bottom', horizontal: 'center', message})();
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = (mailId, fileName) => {
    downloadMailboxAttachment(mailId, fileName, agencieSelected?.id);
  };

  const parseCertificadoRetencionBody = (body) => {
    try {
      const parsed = JSON.parse(body);
      if (parsed?.type === 'certificado-retencion') return parsed;
      return null;
    } catch {
      return null;
    }
  };

  const handleDownloadCertificadoRetention = (mailId) => {
    const mail = mails.find((m) => m.id === mailId);
    const params = parseCertificadoRetencionBody(mail?.body);
    if (!params) return;
    downloadCertificadoRetentionPDF(activeOrganization?.id, params);
  };

  const handleConsulting = (agencie) => {
      if (agencie) {
        agencieSearchByNumber(activeOrganization?.id, {
          number: agencie?.number,
        })
          .then((response) => {
            dispatch(setConcentrator(response));
            dispatch(setSelectedAgencieToConsult(agencie));
          })
          .then(() => {
            setSearch(true);
          })
          .catch((e) => {
            console.log(e);
          })
          .finally(() => { fetchMails(agencie) })
      }
    };

  const handleDelete = (id) => {
    // FN para eliminar mensajes. No implementado.
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
            <Grid display="flex" mb={2} justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">Novedades</Typography>
                {/* <TextField variant="outlined" size="small" placeholder="Buscar" /> */}
                <Tooltip title="Agregar Agencia">
                  <Button 
                    onClick={handleToggleAgencySelectModal}
                    color="primary"
                    className={s.filterButton}
                    variant="outlined"
                    startIcon={<AddBusinessIcon />}
                    sx={{
                      textTransform: 'none',
                      padding: '6px 16px',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      boxShadow: 'none',
                      borderRadius: '8px', 
                    }}
                  >
                    <Typography variant="body2">
                      {`Agencia ${agencieSelected?.number}`}
                    </Typography>
                  </Button>
                </Tooltip>

                {/* Selector de categoría — filtra llamando al backend */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="mailbox-kind-label">Categoría</InputLabel>
                  <Select
                    labelId="mailbox-kind-label"
                    value={selectedKind}
                    label="Categoría"
                    onChange={handleKindChange}
                  >
                    {MAILBOX_KIND_OPTIONS.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* <Box>
                
              </Box> */}

              <Box>
                <Tooltip title="Refrescar">
                  <SyncIcon
                    style={{ cursor: 'pointer' }}
                    sx={{ fontSize: '2rem', color: 'text.secondary' }}
                    onClick={()=>fetchMails(agencieSelected)}
                  />
                </Tooltip>
              </Box>
            </Grid>
            <Box>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Asunto</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Fecha</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', minHeight: 80 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Cargando...</Typography>
                          <CircularProgress size="2rem" />
                        </Box>
                      </td>
                    </tr>
                  ) : (mails?.length === 0 || mails === null || mails === undefined ? (
                    <tr>
                      <td colSpan={4}>
                        <Typography variant="body2">No hay correos</Typography>
                      </td>
                    </tr>
                  ) : (
                    mails?.map((item) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: '1px solid #eee',
                          background: item.isRead ? '#fff' : '#f0faff',
                          fontWeight: item.isRead ? 'normal' : 'bold',
                        }}
                      >
                        <td style={{ padding: '8px', fontWeight: item.isRead ? 'normal' : '700' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {!item.isRead && (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  px: '8px',
                                  py: '2px',
                                  borderRadius: '12px',
                                  backgroundColor: 'primary.main',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  letterSpacing: '0.03em',
                                  lineHeight: 1.4,
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    flexShrink: 0,
                                  }}
                                />
                                Nuevo
                              </Box>
                            )}
                            {item.subject || item.title || '-'}
                          </Box>
                        </td>
                        <td style={{ padding: '8px', fontWeight: item.isRead ? 'normal' : '700' }}>{formatDate(item.created) || '-'}</td>
                        <td style={{ padding: '8px' }}>
                          <VisibilityIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOpen(item.id)}
                          />
                        </td>
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </Box>

            <Box mt={2} display="flex" justifyContent="center">
              <TablePagination
                component="div"
                count={totalElements}
                page={controllerPagination.page}
                onPageChange={handleChangePage}
                rowsPerPage={controllerPagination.size}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ViewMailsModal
        mailId={selectedMailId ?? null}
        title={selectedMailId ? `Detalles del correo ${mails.find(mail => mail.id === selectedMailId)?.subject}` : "Asunto: -"}
        description={(() => {
          const mail = mails.find(m => m.id === selectedMailId);
          if (!mail) return 'Detalle: -';
          const cert = parseCertificadoRetencionBody(mail.body);
          if (cert) return `Período: ${cert.fechaDesde} — ${cert.fechaHasta}\nAgencia: ${cert.agencyName}\nCUIT: ${cert.cuit}`;
          return mail.body ?? 'Detalle: -';
        })()}
        fileName={selectedMailId ? mails.find(mail => mail.id === selectedMailId)?.fileName : null}
        open={open}
        setOpen={setOpen}
        handleOpen={handleOpen}
        handleClose={handleClose}
        handleDownloadAttachment={handleDownloadAttachment}
        isCertificadoRetencion={!!parseCertificadoRetencionBody(mails.find(m => m.id === selectedMailId)?.body)}
        handleDownloadCertificadoRetention={handleDownloadCertificadoRetention}
      />
      <SnackBarError
        state={state}
        handleCloseSnackBarError={handleCloseSnackBarError}
      />
      {
        showHideAgencySelectModal && 
          (<ConcentratorSelectAgencyModal
            open={showHideAgencySelectModal}
            setOpen={handleToggleAgencySelectModal}
            onSelect={handleConsulting}
          />)
      }
    </Container>
  );
};

export default MailList;
